import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { Observable, Subject, throwError } from 'rxjs';
import { catchError, map, takeUntil, tap } from 'rxjs/operators';
import { ParameterThreshold, ThresholdBand } from 'src/app/models/parameter.model';
import { ParameterService } from 'src/app/services/parameter.service';
import { ToastService } from 'src/app/services/toast.service';
import { ThresholdUtils } from 'src/app/shared/utils/threshold.utils';

const DEFAULT_COLOR = '#CCCCCC';
const DEFAULT_BAND_WIDTH = 20; // Percentage

@Component({
  standalone: false,
  selector: 'app-parameter-threshold-editor',
  templateUrl: './parameter-threshold-editor.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./parameter-threshold-editor.component.scss']
})
export class ParameterThresholdEditorComponent implements OnInit, OnDestroy {
  @Input() parameterId: string = '';
  @Input() readonly: boolean = false;
  @Output() saved = new EventEmitter<ParameterThreshold[]>();

  bands: ThresholdBand[] = [];
  isLoading: boolean = false;
  isSaving: boolean = false;
  hasChanges: boolean = false;
  showEmptyState: boolean = false;
  fieldErrors: { [key: string]: string } = {};

  validationErrors: string[] = [];
  previewSegments: Array<{ colorHex: string; flex: number }> = [];
  previewLegendItems: Array<{ colorHex: string; label: string }> = [];
  hasValidationErrorsFlag: boolean = false;

  private destroy$ = new Subject<void>();
  private persistedBandsLoaded = false;

  get hasBands(): boolean {
    return this.bands.length > 0;
  }

  /**
   * True when this parameter had threshold bands persisted in the DB (loaded on init).
   * Used by the parent to distinguish "never had bands" from "had bands, user deleted
   * them all" — the API rejects an empty payload, so the latter must not be saved silently.
   */
  get hadPersistedBands(): boolean {
    return this.persistedBandsLoaded;
  }

  constructor(
    private parameterService: ParameterService,
    private toastService: ToastService,
  ) {}

  ngOnInit(): void {
    if (this.parameterId) {
      this.loadThresholds();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadThresholds(): void {
    this.isLoading = true;
    this.parameterService.getThresholds(this.parameterId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (thresholds) => {
          if (thresholds && thresholds.length > 0) {
            this.bands = thresholds
              .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0))
              .map((t, idx) => ({
                id: t.id,
                key: `band_${idx}`,
                label: t.label || `Band ${idx + 1}`,
                minValue: t.minValue ?? 0,
                maxValue: t.maxValue ?? null,
                colorHex: t.colorHex || DEFAULT_COLOR,
                sortOrder: idx + 1,
                noUpperBound: t.maxValue === null
              }));
            this.persistedBandsLoaded = true;
            this.showEmptyState = false;
          } else {
            this.bands = [];
            this.persistedBandsLoaded = false;
            this.showEmptyState = true;
          }
          this.updateComputedProperties();
          this.isLoading = false;
        },
        error: (_error) => {
          this.toastService.displayErrorMessage('Failed to load thresholds', true);
          this.isLoading = false;
        }
      });
  }

  addBand(): void {
    const lastBand = this.bands[this.bands.length - 1];

    // Prevent adding bands after an infinity band
    if (lastBand && lastBand.noUpperBound) {
      this.toastService.displayErrorMessage('Cannot add bands after an infinity (∞) band. Remove or modify the infinity band first.');
      return;
    }

    const newMinValue = lastBand ? (lastBand.maxValue || 0) : 0;

    const newBand: ThresholdBand = {
      key: `band_${Date.now()}`,
      label: `Band ${this.bands.length + 1}`,
      minValue: newMinValue,
      maxValue: newMinValue + 50,
      colorHex: DEFAULT_COLOR,
      sortOrder: this.bands.length + 1,
      noUpperBound: false
    };

    this.bands.push(newBand);
    this.hasChanges = true;
    this.showEmptyState = false;
    this.updateComputedProperties();
  }

  deleteBand(index: number): void {
    this.bands.splice(index, 1);

    // Update sort order and auto-fix next band's minValue
    this.bands.forEach((band, idx) => {
      band.sortOrder = idx + 1;
      if (idx > 0) {
        const prevBand = this.bands[idx - 1];
        band.minValue = prevBand.maxValue || prevBand.minValue;
      }
    });

    if (this.bands.length === 0) {
      this.showEmptyState = true;
    }
    this.markChangedAndValidate();
  }

  toggleInfinity(index: number): void {
    if (index !== this.bands.length - 1) {
      return; // Infinity only allowed on last band
    }

    const band = this.bands[index];
    band.noUpperBound = !band.noUpperBound;

    if (band.noUpperBound) {
      band.maxValue = null;
    } else {
      band.maxValue = band.minValue + 50;
    }

    this.markChangedAndValidate();
  }

  onMinValueChange(index: number): void {
    this.markChangedAndValidate();
  }

  onMaxValueChange(index: number): void {
    if (this.bands[index].maxValue !== null) {
      this.bands[index].noUpperBound = false;
    }
    this.markChangedAndValidate();
  }

  onColorChange(index: number): void {
    this.hasChanges = true;
    this.updateComputedProperties();
  }

  validateBands(): boolean {
    this.fieldErrors = {};

    for (let i = 0; i < this.bands.length; i++) {
      const band = this.bands[i];
      const key = `band_${i}`;

      // Check for infinity band that is not final
      if (band.noUpperBound && i !== this.bands.length - 1) {
        this.fieldErrors[`${key}_max`] = 'Infinity (∞) can only be set on the last band';
      }

      if (band.minValue == null || isNaN(band.minValue)) {
        this.fieldErrors[`${key}_min`] = 'Invalid minimum value';
      }

      // Check for invalid max value (if not infinity)
      if (!band.noUpperBound && band.maxValue !== null && isNaN(band.maxValue)) {
        this.fieldErrors[`${key}_max`] = 'Invalid maximum value';
      }

      // Check for minValue >= maxValue on non-infinity bands
      if (!band.noUpperBound && band.maxValue !== null && band.minValue >= band.maxValue) {
        this.fieldErrors[`${key}_range`] = 'Minimum must be less than maximum';
      }

      // Check for overlap with previous band
      if (i > 0) {
        const prevBand = this.bands[i - 1];
        // Mirror the API's contiguity tolerance (BulkSaveAsync uses Math.Abs(...) > 0.001)
        // so floating-point inputs don't trigger phantom errors the BE would accept.
        if (Math.abs(band.minValue - (prevBand.maxValue ?? 0)) > 0.001) {
          this.fieldErrors[`${key}_min`] = `Should be ${prevBand.maxValue}`;
        }
      }
    }

    this.updateComputedProperties();
    return Object.keys(this.fieldErrors).length === 0;
  }

  persistThresholds(parameterId: string): Observable<ParameterThreshold[]> {
    this.parameterId = parameterId;
    const thresholdsToSave = this.buildThresholdPayload(parameterId);
    this.isSaving = true;

    return this.parameterService.bulkSaveThresholds(parameterId, thresholdsToSave).pipe(
      takeUntil(this.destroy$),
      tap(() => {
        this.saved.emit(thresholdsToSave);
        this.hasChanges = false;
        this.isSaving = false;
      }),
      map(() => thresholdsToSave),
      catchError((error: unknown) => {
        this.isSaving = false;
        return throwError(() => error);
      })
    );
  }

  private buildThresholdPayload(parameterId: string): ParameterThreshold[] {
    return this.bands.map((band, idx) => ({
      id: band.id,
      parameterId,
      sortOrder: idx + 1,
      label: band.label || `Band ${idx + 1}`,
      minValue: band.minValue,
      // API requires the last threshold to be open-ended.
      maxValue: idx === this.bands.length - 1 ? null : band.maxValue,
      colorHex: band.colorHex
    } as ParameterThreshold));
  }

  private markChangedAndValidate(): void {
    this.hasChanges = true;
    this.validateBands();
  }

  private updateComputedProperties(): void {
    this.validationErrors = Object.values(this.fieldErrors).filter((err, idx, arr) => arr.indexOf(err) === idx);
    this.hasValidationErrorsFlag = this.validationErrors.length > 0;

    this.previewSegments = this.bands.map((band, idx) => ({
      colorHex: band.colorHex,
      flex: this.calculateSegmentWidth(band, idx)
    }));

    this.previewLegendItems = this.bands.map(band => ({
      colorHex: band.colorHex,
      label: ThresholdUtils.formatLabel(band.minValue, band.maxValue, band.noUpperBound || false)
    }));
  }

  private calculateSegmentWidth(band: ThresholdBand, index: number): number {
    const isLastBand = index === this.bands.length - 1;

    if (isLastBand && band.noUpperBound) {
      return DEFAULT_BAND_WIDTH;
    }

    const range = band.maxValue !== null ? band.maxValue - band.minValue : 100;
    const totalRange = ThresholdUtils.calculateTotalRange(this.bands);

    if (totalRange === 0) return DEFAULT_BAND_WIDTH;
    return (range / totalRange) * 100;
  }

  trackByBandIndex(index: number, band: ThresholdBand): string {
    return band.key;
  }

  trackByErrorIndex(index: number, error: string): string {
    return error;
  }

  trackBySegmentIndex(index: number, segment: { colorHex: string; flex: number }): string {
    return segment.colorHex + index;
  }
}
